import { useState } from "react";
import {
  RtkQueryMutation,
  useDebouncedMutation,
} from "@/shared/lib/debounce/debounce";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import { Representative } from "@/services/backend/representatives/types/representative";
import { FieldErrors } from "@/shared/lib/errors/validation-error";
import { joinFieldName } from "@/shared/lib/errors/err-validation-failed-context";
import { TextInputField } from "@/shared/components/field/input-field";

interface RepresentativeCardProps {
  representative: Representative;
  onRepresentativeChange: (representative: Representative) => void;
  title?: string;
  description?: string;
  /**
   * Additional errors, keyed relative to the representative (e.g. "email"),
   * merged with the errors of the ErrValidationFailedContext.
   */
  fieldErrors?: FieldErrors;
  /**
   * The field name of the representative in the ErrValidationFailedContext. Its
   * fields are read from and reset at `${fieldName}.<field>`, e.g.
   * "representative.email".
   */
  fieldName?: string;
  disabled?: boolean;
}

export function RepresentativeCard({
  representative,
  onRepresentativeChange,
  title = t("Betreuer"),
  description = t("Kontakt für Rückfragen des Kunden."),
  fieldErrors = undefined,
  fieldName = "representative",
  disabled = false,
}: RepresentativeCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 lg:grid-cols-3">
        <TextInputField
          label={t("Name")}
          text={representative.name}
          onTextChange={(name) =>
            onRepresentativeChange({ ...representative, name })
          }
          fieldName={joinFieldName(fieldName, "name")}
          errors={fieldErrors?.name}
          disabled={disabled}
          fieldProps={{ className: "col-span-full" }}
        />
        <TextInputField
          label={t("Mobil")}
          text={representative.mobilephone}
          onTextChange={(mobilephone) =>
            onRepresentativeChange({ ...representative, mobilephone })
          }
          fieldName={joinFieldName(fieldName, "mobilephone")}
          errors={fieldErrors?.mobilephone}
          disabled={disabled}
        />
        <TextInputField
          label={t("Telefon")}
          text={representative.phone}
          onTextChange={(phone) =>
            onRepresentativeChange({ ...representative, phone })
          }
          fieldName={joinFieldName(fieldName, "phone")}
          errors={fieldErrors?.phone}
          disabled={disabled}
        />
        <TextInputField
          label={t("E-Mail")}
          text={representative.email}
          onTextChange={(email) =>
            onRepresentativeChange({ ...representative, email })
          }
          fieldName={joinFieldName(fieldName, "email")}
          errors={fieldErrors?.email}
          disabled={disabled}
        />
      </CardContent>
    </Card>
  );
}

type Mutation<Req, Res> = () => RtkQueryMutation<Req, Res>;

interface RepresentativeCardWithMutationProps<Req, Res> {
  id: string;
  representative: Representative;
  mutation: Mutation<Req, Res>;
  title?: string;
  description?: string;
  /** See {@link RepresentativeCard}. */
  fieldErrors?: FieldErrors;
  /** See {@link RepresentativeCard}. Defaults to "representative". */
  fieldName?: string;
  disabled?: boolean;
}

/**
 * RepresentativeCardWithMutation is a {@link RepresentativeCard} saving the
 * representative with a PUT of `{ id, representative }`. Its validation
 * errors are written into the nearest ErrValidationFailedContextProvider,
 * which the page mounts. Invalid input is kept after a failed save.
 */
export function RepresentativeCardWithMutation<
  Req extends { id: string; representative: Representative },
  Res,
>({
  id,
  representative,
  mutation,
  title = t("Betreuer"),
  description = t("Kontakt für Rückfragen des Kunden."),
  fieldErrors = undefined,
  fieldName = "representative",
  disabled = false,
}: RepresentativeCardWithMutationProps<Req, Res>) {
  // @ts-expect-error - I do not understand the error. But it works.
  const [request, setRequest] = useState<Req>({ id, representative });
  const debouncedMutation = mutation();
  useDebouncedMutation(
    request,
    setRequest,
    debouncedMutation,
    // @ts-expect-error - I do not understand the error. But it works.
    { id, representative },
    {
      resetOnError: false,
    },
  );

  return (
    <RepresentativeCard
      representative={request.representative}
      // @ts-expect-error - I do not understand the error. But it works.
      onRepresentativeChange={(r) => setRequest({ id, representative: r })}
      title={title}
      description={description}
      fieldErrors={fieldErrors}
      fieldName={fieldName}
      disabled={disabled}
    />
  );
}

export function RepresentativeCardSkeleton({
  title = t("Betreuer"),
  description = "Kontakt für Rückfragen des Kunden.",
}: {
  title?: string;
  description?: string;
}) {
  return (
    <Card className="animate-pulse">
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
      </CardHeader>
    </Card>
  );
}
